import React, { useState } from "react";
import { useT } from "../../lib/i18n";

const EVENT_STYLE = {
  USER_REGISTERED: "chip-IIa",
  USER_LOGIN: "chip-I",
  USER_LOGOUT: "chip-I",
  PROJECT_CREATED: "chip-IIa",
  PROJECT_DELETED: "chip-III",
  ANALYSIS_STARTED: "chip-IIb",
  ANALYSIS_VERSION_CREATED: "chip-IIa",
  VERSION_FROZEN: "chip-I",
  PDF_EXPORTED: "chip-IIa",
  DOCUMENT_SIGNED: "chip-I",
  DOCUMENT_DOWNLOADED: "chip-IIa",
};

// Events that are infrastructure plumbing — not shown to the end user.
const HIDDEN_EVENTS = new Set(["AI_REQUEST_SENT", "AI_RESPONSE_RECEIVED", "ANALYSIS_STARTED"]);

export default function AuditTab({ logs }) {
  const t = useT();
  const [filter, setFilter] = useState("");

  // Product-level filtering: remove infrastructure events
  const cleaned = logs.filter(l => !HIDDEN_EVENTS.has(l.event_type));
  const filtered = cleaned.filter(l => !filter || l.event_type === filter);

  function eventLabel(ev) {
    return t(`audit.ev.${ev}`) !== `audit.ev.${ev}` ? t(`audit.ev.${ev}`) : ev.replace(/_/g, " ").toLowerCase();
  }

  function renderDetail(l) {
    const src = l.after || l.before || {};
    if (l.event_type === "PROJECT_CREATED" && src.device_name) return `${src.name} · ${src.device_name}`;
    if (l.event_type === "ANALYSIS_VERSION_CREATED") return `v${src.version} · ${t("ov.suggested")}: ${src.class || "—"}`;
    if (l.event_type === "VERSION_FROZEN") return `v${src.version} · SHA-256 ${String(src.signature_hash || "").slice(0, 16)}…`;
    if (l.event_type === "PDF_EXPORTED") return `v${src.version}`;
    if (l.event_type === "DOCUMENT_SIGNED" || l.event_type === "DOCUMENT_DOWNLOADED") return src.document || src.name || "";
    if (l.event_type === "USER_REGISTERED" || l.event_type === "USER_LOGIN") return src.email || "";
    if (l.event_type === "PROJECT_DELETED") return src?.name || "";
    return "";
  }

  return (
    <div className="space-y-3" data-testid="audit-table">
      <div className="flex items-center justify-between">
        <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">{t("audit.immutable")} · {cleaned.length} {t("audit.entries")}</div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="audit-filter"
          className="border border-zinc-200 px-3 py-1.5 rounded-sm text-xs font-mono bg-white focus:border-blue-600 focus:outline-none">
          <option value="">{t("audit.all")}</option>
          {Object.keys(EVENT_STYLE).map(k => <option key={k} value={k}>{eventLabel(k)}</option>)}
        </select>
      </div>
      <div className="grid-card p-0 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left font-mono">
              <th className="px-4 py-2 uppercase tracking-wider w-44">{t("audit.ts")}</th>
              <th className="px-4 py-2 uppercase tracking-wider">{t("audit.event")}</th>
              <th className="px-4 py-2 uppercase tracking-wider w-32">{t("audit.user")}</th>
              <th className="px-4 py-2 uppercase tracking-wider">{t("audit.payload")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((l) => (
              <tr key={l.id} className="audit-row border-b border-zinc-100">
                <td className="px-4 py-2.5 font-mono text-zinc-500">{l.timestamp.slice(0, 19).replace("T", " ")}</td>
                <td className="px-4 py-2.5">
                  <span className={`chip ${EVENT_STYLE[l.event_type] || "chip-IIa"}`}>{eventLabel(l.event_type)}</span>
                </td>
                <td className="px-4 py-2.5 font-mono text-zinc-600">{l.user_id?.slice(0, 8)}</td>
                <td className="px-4 py-2.5 text-zinc-700">{renderDetail(l)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="p-6 text-center text-zinc-500 text-sm">{t("audit.empty")}</div>}
      </div>
    </div>
  );
}
